import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send, Loader2, Leaf, Trash2, Plus, MessageSquare,
  CheckCircle2, Circle, Pencil, X, Check, Copy,
  PanelRightOpen, PanelRightClose, // For logs panel toggle (currently hidden)
  Share2,
} from 'lucide-react';
import ChatShareModal from './components/ChatShareModal';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/Sidebar';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

const NATURAL_ORGANIC_ID = Number(import.meta.env.VITE_NATURAL_ORGANIC_SEGMENT_ID || 222);
const FLAVCITY_ID = Number(import.meta.env.VITE_FLAVCITY_SEGMENT_ID || 385);
const KNF_SURVEY_ID = Number(import.meta.env.VITE_KNF_SURVEY_SEGMENT_ID || 390);
const SMARTYPANTS_ID = Number(import.meta.env.VITE_SMARTYPANTS_SEGMENT_ID || 400);
const ALT_MILK_ID = Number(import.meta.env.VITE_ALT_MILK_SEGMENT_ID || 1169);

const AVAILABLE_SEGMENTS = [
  { id: NATURAL_ORGANIC_ID, name: 'Natural Organic Shoppers', icon: Leaf },
  { id: FLAVCITY_ID, name: 'FlavCity Protein Consumers', icon: Leaf },
  { id: KNF_SURVEY_ID, name: 'Kettle & Fire Survey', icon: Leaf },
  { id: SMARTYPANTS_ID, name: 'SmartyPants Consumers', icon: Leaf },
  { id: ALT_MILK_ID, name: 'Alt Milk Consumers', icon: Leaf },
];

const DEFAULT_SEGMENT_ID = NATURAL_ORGANIC_ID;

// ── Types ────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  personas_cited?: string[];
  format?: string;
  progress_snapshot?: StructuredProgress; // Saved progress for display in chat history
}

interface ChatSession {
  id: number;
  title: string | null;
  message_count: number;
  created_at: string | null;
  updated_at: string | null;
}

// Progress tracking types
interface StageInfo {
  stage: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
  group?: 'planning' | 'output';  // Group for visual organization
}

interface PlanStepProgress {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'skipped';
}

interface StructuredProgress {
  current_stage: string;
  stages: StageInfo[];
  plan_steps?: PlanStepProgress[];
  plan_version: number;
  message: string;
}

interface TaskStatusResp {
  task_id: string;
  state: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
  progress?: number;
  message?: string;
  structured_progress?: StructuredProgress;
  result?: {
    response: string;
    format: string;
    personas_cited: string[];
  };
  error?: string;
}

interface LogEntry {
  id: number;
  text: string;
  agent: string;
  label: string;
  isHeader: boolean;
  isSeparator: boolean;
}

// ── Time-based progress: NEVER goes backwards ──

// Calculate progress with configurable duration
// Fast tasks (analyzing, executing): 60 seconds to ~80%
// Slow tasks (planning, output): 4 minutes (240 seconds) to ~95%
const calculateProgress = (
  startTime: number,
  maxProgress: number = 80,
  durationMs: number = 60000
): number => {
  const elapsed = Date.now() - startTime;

  // Use easing function for natural feel
  const t = Math.min(elapsed / durationMs, 1);

  // Ease-out curve: fast start, slows down as approaching max
  // Formula: 1 - (1-t)^2 gives nice ease-out
  const eased = 1 - Math.pow(1 - t, 2);

  return eased * maxProgress;
};

// Duration constants
const FAST_DURATION = 60000;   // 60 seconds for fast stages
const SLOW_DURATION = 240000;  // 4 minutes for planning/output

// ── Time-based progress bar component ──
const TimeBasedProgressBar: React.FC<{
  taskId: string;
  startTime: number | undefined;
  isCompleted: boolean;
}> = ({ taskId, startTime, isCompleted }) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const maxReachedRef = useRef(0); // CRITICAL: Track max progress reached - never go below this
  const effectiveStartTimeRef = useRef<number | null>(null); // Capture start time on first active render

  useEffect(() => {
    if (isCompleted) {
      // Animate to 100% smoothly
      const animateTo100 = () => {
        setDisplayProgress(prev => {
          if (prev >= 99.5) {
            maxReachedRef.current = 100;
            return 100;
          }
          const next = prev + (100 - prev) * 0.2;
          maxReachedRef.current = Math.max(maxReachedRef.current, next);
          return next;
        });
      };
      const interval = setInterval(animateTo100, 50);
      setTimeout(() => {
        clearInterval(interval);
        setDisplayProgress(100);
        maxReachedRef.current = 100;
      }, 500);
      return () => clearInterval(interval);
    }

    // Use provided startTime OR capture current time on first active render
    // This fixes the bug where startTime is undefined due to React async state updates
    if (!effectiveStartTimeRef.current) {
      effectiveStartTimeRef.current = startTime || Date.now();
    }
    const effectiveStartTime = effectiveStartTimeRef.current;

    // Use slow duration (4 min) for final_output, fast (60s) for other tasks
    const isSlowTask = taskId === 'final_output';
    const duration = isSlowTask ? SLOW_DURATION : FAST_DURATION;
    const hashCode = taskId.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0);
    const maxProgress = isSlowTask ? 90 + (Math.abs(hashCode) % 8) : 75 + (Math.abs(hashCode) % 10); // 90-98% or 75-85%

    const interval = setInterval(() => {
      const targetProgress = calculateProgress(effectiveStartTime, maxProgress, duration);

      // CRITICAL: Never go below the maximum we've reached
      const newProgress = Math.max(maxReachedRef.current, targetProgress);
      maxReachedRef.current = newProgress;

      // Smooth transition towards target
      setDisplayProgress(prev => {
        const diff = newProgress - prev;
        if (Math.abs(diff) < 0.3) return newProgress;
        // Move 20% of the way each tick for smooth animation
        return prev + diff * 0.2;
      });
    }, 100); // Update every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [startTime, isCompleted, taskId]);

  const showProgress = Math.round(displayProgress);

  return (
    <div className="mt-2">
      <div className="h-1.5 bg-[#07E5D1]/20 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#07E5D1] rounded-full transition-all duration-300 ease-out"
          style={{ width: `${showProgress}%` }}
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <p className="text-[10px] text-[#07E5D1] flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          {showProgress < 30 ? 'Starting...' : showProgress < 60 ? 'Processing...' : 'Almost there...'}
        </p>
        <span className="text-[10px] text-gray-400">{showProgress}%</span>
      </div>
    </div>
  );
};

// ── Collapsible Progress Accordion for chat history ──
// Only shows the execution plan steps (the actual work done), not static stages
const ProgressAccordion: React.FC<{ progress: StructuredProgress }> = ({ progress }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const planSteps = progress.plan_steps || [];

  // Don't render if no plan steps
  if (planSteps.length === 0) return null;

  return (
    <div className="mt-3 border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2.5 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#07E5D1]" />
          <span className="text-xs font-medium text-gray-700">
            Completed {planSteps.length} tasks
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400">View execution plan</span>
          {isExpanded ? (
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 py-3 bg-white border-t border-gray-100">
          <ul className="space-y-1.5">
            {planSteps.map((step, idx) => (
              <li key={`${step.id}-${idx}`} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#07E5D1] flex-shrink-0 mt-0.5" />
                <span className="text-xs text-gray-500 line-through">{step.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const AGENT_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  ORCHESTRATOR:      { bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500' },
  QUERY_ANALYZER:    { bg: 'bg-purple-50',  text: 'text-purple-700',  dot: 'bg-purple-500' },
  PLANNING_AGENT:    { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  EXECUTION_AGENT:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  OUTPUT_ANALYST:    { bg: 'bg-pink-50',    text: 'text-pink-700',    dot: 'bg-pink-500' },
  GLOBAL:            { bg: 'bg-gray-50',    text: 'text-gray-600',    dot: 'bg-gray-400' },
};

function getAgentColor(agent: string) {
  return AGENT_COLORS[agent] || { bg: 'bg-gray-50', text: 'text-gray-600', dot: 'bg-gray-400' };
}

let _logId = 0;
function parseLine(raw: string): LogEntry {
  _logId += 1;
  const trimmed = raw.trim();
  if (/^[═─]{10,}/.test(trimmed)) {
    return { id: _logId, text: '', agent: '', label: '', isHeader: false, isSeparator: true };
  }
  const headerMatch = trimmed.match(/^\[(\d{2}:\d{2}:\d{2}\.\d{3})\]\s+([A-Z_]+)\s+\|\s+(.+)$/);
  if (headerMatch) {
    const [, time, agent, label] = headerMatch;
    return { id: _logId, text: `${time}`, agent, label, isHeader: true, isSeparator: false };
  }
  return { id: _logId, text: trimmed, agent: '', label: '', isHeader: false, isSeparator: false };
}

// ── Markdown components ──────────────────────────────────────────────────

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1C1C1E] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-[#1C1C1E] mt-3 mb-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-[#1C1C1E] mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-[#374151] mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-[#374151] leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[#1C1C1E]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-[#FAFBFC] rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-[#FAFBFC] text-[#374151] px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a href={href} className="text-[#07E5D1] hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-[#E5E7EB]" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-[#E5E7EB]">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-[#F1F5F9]">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-[#FAFBFC] transition-colors">{children}</tr>,
  th: ({ children }) => (
    <th className="px-4 py-2.5 text-left text-xs font-semibold text-[#374151] uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 text-sm text-[#374151] align-top">{children}</td>
  ),
};

// ── Main Component ───────────────────────────────────────────────────────

const AskNaturalOrganicChat: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const { isAuthenticated, isAdmin, isFlavcityUser, isKnfUser, isSmartypantsUser, isDemoUser } = useAuth();

  // Segment selection: restricted users see only their segment, admin sees dropdown, everyone else Natural Organic
  // Demo users always see Natural Organic (DEFAULT_SEGMENT_ID)
  const userDefaultSegmentId = isSmartypantsUser ? SMARTYPANTS_ID : isKnfUser ? KNF_SURVEY_ID : isFlavcityUser ? FLAVCITY_ID : DEFAULT_SEGMENT_ID;

  // Restricted users (non-admin) only see their own segment - no dropdown
  // Demo users are restricted to Natural Organic only
  const isRestrictedUser = !isAdmin && (isSmartypantsUser || isFlavcityUser || isKnfUser || isDemoUser);
  const [selectedSegmentId, setSelectedSegmentId] = useState<number>(userDefaultSegmentId);
  const selectedSegment = AVAILABLE_SEGMENTS.find(s => s.id === selectedSegmentId) || AVAILABLE_SEGMENTS[0];

  // Session state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSessionLoading, setIsSessionLoading] = useState(false);

  // Input / loading
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Analyzing your question...');
  const [structuredProgress, setStructuredProgress] = useState<StructuredProgress | null>(null);
  const [stepStartTimes, setStepStartTimes] = useState<Record<string, number>>({});
  const [stageStartTimes, setStageStartTimes] = useState<Record<string, number>>({});
  const [lastProgressSnapshot, setLastProgressSnapshot] = useState<StructuredProgress | null>(null);
  const progressSnapshotRef = useRef<StructuredProgress | null>(null); // Sync ref for immediate access in SUCCESS handler

  // Animated dots for loading states (cycles: "" -> "." -> ".." -> "..." -> "")
  const [animatedDots, setAnimatedDots] = useState('');
  useEffect(() => {
    if (!isLoading) { setAnimatedDots(''); return; }
    const interval = setInterval(() => {
      setAnimatedDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);
    return () => clearInterval(interval);
  }, [isLoading]);

  const [showLogs, setShowLogs] = useState(true);
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const logOffsetRef = useRef(0);
  const logFileRef = useRef<string | null>(null);
  const logScrollRef = useRef<HTMLDivElement>(null);
  const logPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Refs
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // Scroll chat to bottom
  const scrollToBottom = useCallback(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, []);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentTaskIdRef = useRef<string | null>(null);
  const pollIntervalRef = useRef<number>(2000);

  // Rename session state
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  // Delete confirmation state
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState<{
    share_token: string;
    share_url: string;
    message_count: number;
    view_count: number;
    fork_count: number;
  } | null>(null);
  const [isCreatingShare, setIsCreatingShare] = useState(false);
  const [isRevokingShare, setIsRevokingShare] = useState(false);

  // ── Auth guard ───────────────────────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) navigate('/auth');
  }, [isAuthenticated, navigate]);

  // Focus input
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);


  // ── Session CRUD ─────────────────────────────────────────────────

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions?segment_id=${selectedSegmentId}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch { /* ignore */ }
  }, [selectedSegmentId]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Check if a session has an active task and resume polling if so
  const checkSessionStatus = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${sessionId}/status`, { credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      return data; // { has_active_task: boolean, task_id?: string }
    } catch {
      return null;
    }
  }, []);

  // Store refs to polling functions (defined later) for use in effects
  const pollTaskStatusRef = useRef<((taskId: string) => Promise<void>) | null>(null);
  const startLogPollingRef = useRef<((sessionId: number) => void) | null>(null);

  // Stop any active polling (used when switching sessions)
  const stopAllPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (logPollRef.current) {
      clearInterval(logPollRef.current);
      logPollRef.current = null;
    }
    currentTaskIdRef.current = null;
    setIsLoading(false);
    setStructuredProgress(null);
    setProgressMessage('');
  }, []);

  const createSession = useCallback(async () => {
    stopAllPolling();

    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ segment_id: selectedSegmentId }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setActiveSessionId(data.id);
      setMessages([]);
      await fetchSessions();
    } catch { /* ignore */ }
  }, [fetchSessions, stopAllPolling, selectedSegmentId]);

  const loadSession = useCallback(async (id: number) => {
    // Stop any existing polling before switching
    stopAllPolling();
    setIsSessionLoading(true);

    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${id}`, { credentials: 'include' });
      if (!res.ok) {
        setIsSessionLoading(false);
        return;
      }
      const data = await res.json();
      setActiveSessionId(id);
      // URL will be updated by the effect that watches activeSessionId
      setMessages(
        (data.messages || []).map((m: any) => ({
          role: m.role,
          content: m.content,
          personas_cited: m.metadata?.personas_cited,
          format: m.metadata?.format,
          progress_snapshot: m.metadata?.progress_snapshot,
        }))
      );

      // Check if there's an active task and resume polling
      const status = await checkSessionStatus(id);
      if (status?.has_active_task && status.task_id) {
        setIsLoading(true);
        setProgressMessage('Resuming...');
        setStructuredProgress({
          current_stage: 'resuming',
          message: 'Resuming...',
          plan_steps: [],
        });
        currentTaskIdRef.current = status.task_id;
        if (pollRef.current) clearInterval(pollRef.current);
        const taskId = status.task_id;
        pollRef.current = setInterval(() => {
          pollTaskStatusRef.current?.(taskId);
        }, 1000);
        pollTaskStatusRef.current?.(taskId);
        startLogPollingRef.current?.(id);
      }
    } catch { /* ignore */ }
    setIsSessionLoading(false);
  }, [stopAllPolling, checkSessionStatus]);

  // Track if we've initialized from URL to prevent loops
  const hasInitializedFromUrlRef = useRef(false);

  // ONE-TIME: On mount, if URL has session ID, load that session
  useEffect(() => {
    if (hasInitializedFromUrlRef.current) return;
    if (!urlSessionId || sessions.length === 0) return;

    hasInitializedFromUrlRef.current = true;
    const sessionIdNum = parseInt(urlSessionId, 10);
    const sessionExists = sessions.some(s => s.id === sessionIdNum);

    if (sessionExists) {
      // Load session data directly without triggering URL update
      setIsSessionLoading(true);
      (async () => {
        try {
          const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${sessionIdNum}`, { credentials: 'include' });
          if (!res.ok) {
            setIsSessionLoading(false);
            return;
          }
          const data = await res.json();
          setActiveSessionId(sessionIdNum);
          setMessages(
            (data.messages || []).map((m: any) => ({
              role: m.role,
              content: m.content,
              personas_cited: m.metadata?.personas_cited,
              format: m.metadata?.format,
              progress_snapshot: m.metadata?.progress_snapshot,
            }))
          );

          // Check if there's an active task and resume polling
          const status = await checkSessionStatus(sessionIdNum);
          if (status?.has_active_task && status.task_id) {
            setIsLoading(true);
            setProgressMessage('Resuming...');
            setStructuredProgress({
              current_stage: 'resuming',
              message: 'Resuming...',
              plan_steps: [],
            });
            currentTaskIdRef.current = status.task_id;
            if (pollRef.current) clearInterval(pollRef.current);
            const taskId = status.task_id;
            pollRef.current = setInterval(() => {
              pollTaskStatusRef.current?.(taskId);
            }, 1000);
            pollTaskStatusRef.current?.(taskId);
            startLogPollingRef.current?.(sessionIdNum);
          }
        } catch { /* ignore */ }
        setIsSessionLoading(false);
      })();
    } else {
      // Session doesn't exist, redirect to base URL
      navigate('/ask-natural-organic', { replace: true });
    }
  }, [urlSessionId, sessions, navigate, checkSessionStatus]);

  // Update URL when activeSessionId changes (from user actions, not from URL load)
  useEffect(() => {
    // Skip if this is the initial load from URL
    if (!activeSessionId) return;

    const currentUrlId = urlSessionId ? parseInt(urlSessionId, 10) : null;
    if (currentUrlId !== activeSessionId) {
      navigate(`/ask-natural-organic/${activeSessionId}`, { replace: true });
    }
  }, [activeSessionId, navigate, urlSessionId]);

  const deleteSession = useCallback(async (id: number) => {
    try {
      await fetch(`${API_URL}/healthcare-chat-v2/sessions/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (activeSessionId === id) {
        stopAllPolling();
        setActiveSessionId(null);
        setMessages([]);
        navigate('/ask-natural-organic', { replace: true });
      }
      await fetchSessions();
    } catch { /* ignore */ }
  }, [activeSessionId, fetchSessions, navigate, stopAllPolling]);

  // ── Rename session handlers ──
  const handleStartRename = useCallback((session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setEditingTitle(session.title || `Chat ${session.id}`);
  }, []);

  const handleSaveRename = useCallback(async () => {
    if (!editingSessionId || !editingTitle.trim()) {
      setEditingSessionId(null);
      return;
    }
    setIsRenaming(true);
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions/${editingSessionId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editingTitle.trim() }),
      });
      if (res.ok) {
        await fetchSessions();
      }
    } catch { /* ignore */ }
    setIsRenaming(false);
    setEditingSessionId(null);
  }, [editingSessionId, editingTitle, fetchSessions]);

  const handleCancelRename = useCallback(() => {
    setEditingSessionId(null);
    setEditingTitle('');
  }, []);

  const handleRenameKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveRename();
    } else if (e.key === 'Escape') {
      handleCancelRename();
    }
  }, [handleSaveRename, handleCancelRename]);

  // ── Delete confirmation handlers ──
  const handleDeleteClick = useCallback((id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingSessionId(id);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deletingSessionId) {
      await deleteSession(deletingSessionId);
      setDeletingSessionId(null);
    }
  }, [deletingSessionId, deleteSession]);

  const handleCancelDelete = useCallback(() => {
    setDeletingSessionId(null);
  }, []);

  // ── Share Functions ─────────────────────────────────────────────────

  const fetchExistingShare = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${sessionId}/share`,
        { credentials: 'include' }
      );
      if (!res.ok) return null;
      const data = await res.json();
      if (data.has_share) {
        return {
          share_token: data.share_token,
          share_url: data.share_url,
          message_count: data.message_count,
          view_count: data.view_count || 0,
          fork_count: data.fork_count || 0,
        };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const handleShareClick = useCallback(async () => {
    if (!activeSessionId || messages.length === 0) return;

    setIsCreatingShare(true);
    try {
      // First check if there's an existing share
      const existing = await fetchExistingShare(activeSessionId);
      if (existing) {
        setShareData(existing);
        setShowShareModal(true);
        setIsCreatingShare(false);
        return;
      }

      // Create a new share
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${activeSessionId}/share`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ allow_fork: true }),
        }
      );

      if (!res.ok) {
        console.error('Failed to create share link');
        return;
      }

      const data = await res.json();
      setShareData({
        share_token: data.share_token,
        share_url: data.share_url,
        message_count: data.message_count,
        view_count: 0,
        fork_count: 0,
      });
      setShowShareModal(true);
    } catch (err) {
      console.error('Error creating share:', err);
    } finally {
      setIsCreatingShare(false);
    }
  }, [activeSessionId, messages.length, fetchExistingShare]);

  const handleRevokeShare = useCallback(async () => {
    if (!activeSessionId || !shareData) return;

    setIsRevokingShare(true);
    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${activeSessionId}/share/${shareData.share_token}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (res.ok) {
        setShareData(null);
        setShowShareModal(false);
      }
    } catch (err) {
      console.error('Error revoking share:', err);
    } finally {
      setIsRevokingShare(false);
    }
  }, [activeSessionId, shareData]);

  // Clear share data when session changes
  useEffect(() => {
    setShareData(null);
    setShowShareModal(false);
  }, [activeSessionId]);

  const pollLogs = useCallback(async (sessionId: number) => {
    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${sessionId}/logs?offset=${logOffsetRef.current}`,
        { credentials: 'include' },
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.file && logFileRef.current && data.file !== logFileRef.current) {
        setLogEntries([]);
        _logId = 0;
        logOffsetRef.current = 0;
        logFileRef.current = data.file;
        return;
      }
      logFileRef.current = data.file ?? null;
      if (data.lines && data.lines.length > 0) {
        const newEntries = data.lines.map((ln: string) => parseLine(ln));
        setLogEntries(prev => [...prev, ...newEntries]);
      }
      logOffsetRef.current = data.offset;
    } catch {}
  }, []);

  const startLogPolling = useCallback((sessionId: number) => {
    if (logPollRef.current) clearInterval(logPollRef.current);
    setLogEntries([]);
    _logId = 0;
    logOffsetRef.current = 0;
    logFileRef.current = null;
    logPollRef.current = setInterval(() => pollLogs(sessionId), 1500);
    setTimeout(() => pollLogs(sessionId), 800);
  }, [pollLogs]);

  const stopLogPolling = useCallback(() => {
    if (logPollRef.current) {
      clearInterval(logPollRef.current);
      logPollRef.current = null;
    }
  }, []);

  // ── Task status polling ──────────────────────────────────────────

  const pollTaskStatus = useCallback(async (taskId: string) => {
    try {
      const res = await fetch(`${API_URL}/healthcare-chat-v2/status/${taskId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('status poll failed');
      const status: TaskStatusResp = await res.json();

      if (status.state === 'PENDING' || status.state === 'PROGRESS') {
        setProgressMessage(status.message || 'Processing...');
        if (status.structured_progress) {
          // Track when steps start running
          const newStepStartTimes: Record<string, number> = { ...stepStartTimes };
          status.structured_progress.plan_steps?.forEach(step => {
            if (step.status === 'running' && !newStepStartTimes[step.id]) {
              newStepStartTimes[step.id] = Date.now();
            }
          });
          // Also track when the output stage starts
          const outputStage = status.structured_progress.stages.find(s => s.stage === 'output');
          if (outputStage?.status === 'active' && !newStepStartTimes['final_output']) {
            newStepStartTimes['final_output'] = Date.now();
          }
          setStepStartTimes(newStepStartTimes);

          // Track when stages become active
          const newStageStartTimes: Record<string, number> = { ...stageStartTimes };
          status.structured_progress.stages.forEach(stage => {
            if (stage.status === 'active' && !newStageStartTimes[stage.stage]) {
              newStageStartTimes[stage.stage] = Date.now();
            }
          });
          setStageStartTimes(newStageStartTimes);

          setStructuredProgress(status.structured_progress);
          setLastProgressSnapshot(status.structured_progress);
          progressSnapshotRef.current = status.structured_progress; // Sync update for immediate access in SUCCESS handler
        }
      } else if (status.state === 'SUCCESS' && status.result) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          currentTaskIdRef.current = null;
        }
        stopLogPolling();
        // Create final progress snapshot with all steps completed (including final output step)
        // Use ref for immediate access - not subject to React state timing issues
        const snapshotToUse = progressSnapshotRef.current || lastProgressSnapshot;
        const finalSnapshot: StructuredProgress | undefined = snapshotToUse ? {
          ...snapshotToUse,
          stages: snapshotToUse.stages.map(s => ({ ...s, status: 'completed' as const })),
          plan_steps: [
            ...(snapshotToUse.plan_steps?.map(s => ({ ...s, status: 'completed' as const })) || []),
            { id: 'final_output', label: 'Preparing your answer', status: 'completed' as const }
          ],
        } : undefined;

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: status.result!.response,
          personas_cited: status.result!.personas_cited,
          format: status.result!.format,
          progress_snapshot: finalSnapshot,
        }]);
        setTimeout(scrollToBottom, 50); // Scroll to show new response
        setIsLoading(false);
        setStructuredProgress(null);
        setStepStartTimes({});
        setStageStartTimes({});
        setLastProgressSnapshot(null);
        progressSnapshotRef.current = null; // Clear the ref too
        await fetchSessions();
      } else if (status.state === 'FAILURE') {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          currentTaskIdRef.current = null;
        }
        stopLogPolling();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Sorry, I encountered an error: ${status.error || 'Unknown error'}. Please try again.`,
        }]);
        setTimeout(scrollToBottom, 50); // Scroll to show error message
        setIsLoading(false);
        setStructuredProgress(null);
        setStepStartTimes({});
        setStageStartTimes({});
        setLastProgressSnapshot(null);
        progressSnapshotRef.current = null; // Clear the ref too
      }
    } catch {
      // ignore transient errors
    }
  }, [activeSessionId, fetchSessions, stepStartTimes, stageStartTimes, lastProgressSnapshot, stopLogPolling]);

  // Set refs for polling functions (used by effects that run before these are defined)
  useEffect(() => {
    pollTaskStatusRef.current = pollTaskStatus;
    startLogPollingRef.current = startLogPolling;
  }, [pollTaskStatus, startLogPolling]);

  // ── Send message ─────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    // Ensure we have a session
    let sessionId = activeSessionId;
    if (!sessionId) {
      try {
        const res = await fetch(`${API_URL}/healthcare-chat-v2/sessions`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ segment_id: selectedSegmentId }),
        });
        if (!res.ok) throw new Error('Could not create session');
        const data = await res.json();
        sessionId = data.id;
        setActiveSessionId(sessionId);
        await fetchSessions();
      } catch {
        return;
      }
    }

    const userMsg: ChatMessage = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    // Scroll to bottom after adding user message
    setTimeout(scrollToBottom, 50);
    setIsLoading(true);
    setProgressMessage('Sending query...');
    // Initialize with a minimal structured progress so the thinking bubble shows immediately
    setStructuredProgress({
      current_stage: 'sending',
      message: 'Sending query...',
      plan_steps: [],
    });
    setStepStartTimes({});
    setStageStartTimes({ sending: Date.now() }); // Start tracking sending stage immediately
    progressSnapshotRef.current = null; // Clear any previous snapshot

    try {
      const res = await fetch(
        `${API_URL}/healthcare-chat-v2/sessions/${sessionId}/chat`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: input.trim() }),
        },
      );

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) { navigate('/auth'); return; }
        throw new Error('Failed to send message');
      }

      const data = await res.json();
      const taskId = data.task_id;
      if (!taskId) throw new Error('No task ID received');

      // Poll every 1000ms for faster status updates (was 2000ms)
      currentTaskIdRef.current = taskId;
      pollRef.current = setInterval(() => pollTaskStatus(taskId), 1000);
      pollTaskStatus(taskId);
      startLogPolling(sessionId!);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
      }]);
      setIsLoading(false);
      setStructuredProgress(null);
      setStageStartTimes({});
      setStepStartTimes({});
      progressSnapshotRef.current = null; // Clear the ref
    }
  }, [input, isLoading, activeSessionId, fetchSessions, navigate, pollTaskStatus, startLogPolling, selectedSegmentId]);

  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(null);

  const handleCopyMessage = useCallback(async (content: string, messageIndex: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageIndex(messageIndex);
      setTimeout(() => setCopiedMessageIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  if (!isAuthenticated) return null;

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="h-screen bg-[#FEFEFE] flex overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col md:ml-16 overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-[#F1F5F9] px-6 py-3 mt-16 md:mt-0">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
              <Leaf className="w-4.5 h-4.5" />
            </div>
            <div>
              <h1 className="font-semibold text-[#1C1C1E] text-sm">Ask {selectedSegment.name}</h1>
              <p className="text-xs text-[#9CA3AF]">Always On Focus Group</p>
            </div>
          </div>

          {isAdmin && (
          <div className="flex items-center gap-4">
            <select
              value={selectedSegmentId}
              onChange={(e) => {
                const newId = Number(e.target.value);
                if (newId === selectedSegmentId) return;
                stopAllPolling();
                setSelectedSegmentId(newId);
                setActiveSessionId(null);
                setMessages([]);
              }}
              className="text-xs font-medium px-3 py-1.5 rounded-lg border border-[#E5E7EB] bg-[#FAFBFC] text-[#374151] hover:border-[#07E5D1] transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#07E5D1]"
            >
              {AVAILABLE_SEGMENTS.map(seg => (
                <option key={seg.id} value={seg.id}>{seg.name}</option>
              ))}
            </select>
          </div>
          )}
          
          <div className="flex items-center gap-3">
            {/* Share button - visible when there's an active session with messages */}
            {activeSessionId && messages.length > 0 && (
              <button
                onClick={handleShareClick}
                disabled={isCreatingShare}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[#374151] bg-[#FAFBFC] border border-[#E5E7EB] hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors disabled:opacity-50"
                title="Share this chat"
              >
                {isCreatingShare ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Share2 className="w-3.5 h-3.5" />
                )}
                Share
              </button>
            )}

            <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill" className="h-16" />
            </a>
          </div>
        </div>
      </header>

      {/* Body: sidebar + chat */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar — Sessions */}
        <aside className="w-56 bg-white border-r border-[#F1F5F9] flex flex-col flex-shrink-0">
          <div className="p-3 border-b border-[#F1F5F9]">
            <button
              onClick={createSession}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-[#07E5D1] hover:bg-[#06D4C2] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> New Chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessions.map(s => (
              <div
                key={s.id}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-xs ${
                  activeSessionId === s.id
                    ? 'bg-[#E8FCFA] text-[#07E5D1]'
                    : 'text-[#374151] hover:bg-[#FAFBFC]'
                }`}
                onClick={() => editingSessionId !== s.id && loadSession(s.id)}
              >
                <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />

                {/* Inline edit mode */}
                {editingSessionId === s.id ? (
                  <div className="flex-1 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={handleRenameKeyDown}
                      autoFocus
                      disabled={isRenaming}
                      className="flex-1 px-1.5 py-0.5 text-xs bg-white border border-[#07E5D1] rounded focus:outline-none focus:ring-1 focus:ring-[#07E5D1] text-[#374151]"
                    />
                    <button
                      onClick={handleSaveRename}
                      disabled={isRenaming}
                      className="p-0.5 text-[#10B981] hover:text-[#059669] transition-colors"
                    >
                      {isRenaming ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={handleCancelRename}
                      disabled={isRenaming}
                      className="p-0.5 text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <>
                    <span className="flex-1 truncate">{s.title || `Chat ${s.id}`}</span>
                    <button
                      onClick={(e) => handleStartRename(s, e)}
                      className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#07E5D1] transition-all"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => handleDeleteClick(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 text-[#9CA3AF] hover:text-[#EF4444] transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
            {sessions.length === 0 && (
              <p className="text-xs text-[#9CA3AF] text-center py-8">No sessions yet</p>
            )}
          </div>
        </aside>

        {/* Center — Chat */}
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div ref={chatScrollRef} className="flex-1 overflow-y-auto">
            <div className="max-w-[960px] mx-auto px-6 py-6">
              {/* Session loading state */}
              {isSessionLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-[#07E5D1] animate-spin" />
                    <p className="text-sm text-[#6B7280]">Loading conversation...</p>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!isSessionLoading && messages.length === 0 && !isLoading && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-[#07E5D1] flex items-center justify-center text-white mx-auto mb-5">
                    <Leaf className="w-8 h-8" />
                  </div>
                  <h2 className="text-xl font-bold text-[#1C1C1E] mb-2">Ask {selectedSegment.name}</h2>
                  <p className="text-sm text-[#6B7280] max-w-md mx-auto mb-6">
                  Ask any question and BluePill will gather audience perspectives, analyze, and deliver synthesized insights in minutes.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                {(selectedSegmentId === SMARTYPANTS_ID ? [
                  'What motivates vitamin and supplement purchases?',
                  'How do consumers evaluate supplement brands?',
                  'What are key factors when choosing gummy vitamins?',
                ] : selectedSegmentId === FLAVCITY_ID ? [
                  'What motivates protein product purchases?',
                  'How do consumers evaluate protein brands?',
                  'what is the perception of Flavcity products in the market?',
                ] : selectedSegmentId === ALT_MILK_ID ? [
                  'What motivates milk alternative product purchases?',
                  'How do consumers evaluate milk alternative brands?',
                  'what is the perception of dairy alternative products in the market?',
                ] : [
                  'What motivates natural/organic purchases?',
                  'How do shoppers choose between brands?',
                  'What are common barriers to buying organic?',
                ]).map((s, i) => (
                  <button
                    key={i}
                        onClick={() => setInput(s)}
                        className="px-3.5 py-2 bg-white border border-[#E5E7EB] rounded-xl text-xs text-[#6B7280] hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors"
                  >
                        {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
              {!isSessionLoading && (
              <div className="space-y-5">
            {messages.map((msg, idx) => (
              <React.Fragment key={idx}>
                {/* Progress accordion shown BEFORE assistant message (between query and response) */}
                {msg.role === 'assistant' && msg.progress_snapshot && (
                  <div className="flex justify-start">
                    <div className="max-w-2xl w-full">
                      <ProgressAccordion progress={msg.progress_snapshot} />
                    </div>
                  </div>
                )}
                {/* Message bubble */}
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`rounded-2xl px-5 py-4 ${
                      msg.role === 'user'
                        ? 'max-w-[85%] bg-[#07E5D1] text-white rounded-br-md'
                        : 'max-w-full bg-white border border-[#E5E7EB] text-[#374151] rounded-bl-md shadow-sm'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <div className="max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
                        <div className="flex justify-end mt-3 pt-2 border-t border-[#F1F5F9]">
                          <button
                            onClick={() => handleCopyMessage(msg.content, idx)}
                            className={`flex items-center gap-1.5 px-2 py-1 text-xs transition-colors rounded ${
                              copiedMessageIndex === idx
                                ? 'text-[#10B981] bg-[#ECFDF5]'
                                : 'text-[#9CA3AF] hover:text-[#07E5D1] hover:bg-[#FAFBFC]'
                            }`}
                            title="Copy to clipboard"
                          >
                            {copiedMessageIndex === idx ? (
                              <>
                                <Check className="w-3.5 h-3.5" />
                                Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3.5 h-3.5" />
                                Copy
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                </div>
              </React.Fragment>
            ))}

                {/* Loading with structured progress */}
                {isLoading && (
              <div className="flex justify-start">
                {structuredProgress ? (
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md shadow-sm w-full max-w-2xl overflow-hidden">
                    {/* Header - Flickering dot with execution status */}
                    <div className="px-5 py-3 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-ping flex-shrink-0" />
                          <p className="text-sm text-gray-700 font-medium truncate">
                            {(() => {
                              const steps = structuredProgress.plan_steps || [];
                              const running = steps.filter(s => s.status === 'running').length;

                              if (running > 1) {
                                return `Executing ${running} tasks in parallel${animatedDots}`;
                              }
                              if (running === 1) {
                                return `Executing${animatedDots}`;
                              }
                              // All steps completed or no steps - show backend message
                              return structuredProgress.message || 'Processing...';
                            })()}
                          </p>
                        </div>
                        {/* Percentage fixed on right - cap at 95% until task completes */}
                        {(() => {
                          const steps = structuredProgress.plan_steps || [];
                          const completed = steps.filter(s => s.status === 'completed' || s.status === 'skipped').length;
                          const total = steps.length;
                          if (total > 0) {
                            // Cap at 95% - never show 100% until task actually completes
                            const pct = Math.min(95, Math.round((completed / total) * 100));
                            return <span className="text-sm font-semibold text-[#07E5D1] ml-3 flex-shrink-0">{pct}%</span>;
                          }
                          return null;
                        })()}
                      </div>
                    </div>

                    {/* Executing Plan - only section with progress bars */}
                    {(() => {
                      const planSteps = structuredProgress.plan_steps || [];
                      const executingComplete = planSteps.length > 0 && planSteps.every(s => s.status === 'completed' || s.status === 'skipped');
                      const completedSteps = planSteps.filter(s => s.status === 'completed' || s.status === 'skipped').length;

                      if (planSteps.length === 0) return null;

                      return (
                        <div className="px-5 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Executing Plan
                              </span>
                              {executingComplete && (
                                <CheckCircle2 className="w-3 h-3 text-[#07E5D1]" />
                              )}
                            </div>
                            <span className="text-xs text-gray-400">
                              {completedSteps}/{planSteps.length} done
                            </span>
                          </div>
                          <ul className="space-y-2 ml-1">
                            {planSteps.map((step, idx) => (
                              <li
                                key={`${step.id}-${idx}`}
                                className={`transition-all duration-300 ${
                                  step.status === 'running'
                                    ? 'bg-[#E8FCFA] rounded-lg p-2.5 -mx-1.5 border border-[#07E5D1]/20'
                                    : 'px-0.5'
                                }`}
                              >
                                <div className="flex items-start gap-2.5">
                                  <div className="flex-shrink-0 mt-0.5">
                                    {step.status === 'completed' && (
                                      <CheckCircle2 className="w-4 h-4 text-[#07E5D1]" />
                                    )}
                                    {step.status === 'running' && (
                                      <div className="relative">
                                        <Circle className="w-4 h-4 text-[#07E5D1]" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <div className="w-1.5 h-1.5 bg-[#07E5D1] rounded-full animate-ping" />
                                        </div>
                                      </div>
                                    )}
                                    {(step.status === 'pending' || step.status === 'skipped') && (
                                      <Circle className="w-4 h-4 text-gray-300" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-sm leading-tight block ${
                                      step.status === 'completed'
                                        ? 'text-gray-500 line-through'
                                        : step.status === 'running'
                                        ? 'text-gray-800 font-medium'
                                        : 'text-gray-500'
                                    }`}>
                                      {step.label}
                                    </span>
                                    {step.status === 'running' && (
                                      <TimeBasedProgressBar
                                        taskId={step.id}
                                        startTime={stepStartTimes[step.id]}
                                        isCompleted={false}
                                      />
                                    )}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                    <div className="bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-md px-5 py-4 shadow-sm">
                      <div className="flex items-center gap-3 mb-1">
                        <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                        <span className="text-sm font-medium text-[#374151]">{progressMessage}</span>
                  </div>
                      <p className="text-xs text-[#9CA3AF] ml-7">This may take a moment</p>
                </div>
                )}
              </div>
            )}

                <div className="h-1" />
          </div>
              )}
        </div>
      </div>

          {/* Input */}
          <div className="bg-white border-t border-[#F1F5F9] px-6 py-3">
            <div className="max-w-[960px] mx-auto flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything…"
              disabled={isLoading}
                className="flex-1 px-4 py-3 rounded-xl border border-[#E5E7EB] bg-[#FAFBFC] text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/20 focus:border-[#07E5D1] focus:bg-white transition-all disabled:bg-gray-100 disabled:text-[#9CA3AF]"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
                className="p-3 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </button>
            </div>
          </div>
        </main>

        {/* Logs panel - commented out
        {showLogs && (
          <aside className="w-[420px] bg-white border-l border-[#E5E7EB] flex flex-col flex-shrink-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-[#F1F5F9] flex items-center justify-between">
              <h3 className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Agent Activity</h3>
              <div className="flex items-center gap-3">
                {isLoading && (
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                    <span className="text-[10px] text-[#10B981] font-medium">LIVE</span>
                  </div>
                )}
                <button
                  onClick={() => { setLogEntries([]); logOffsetRef.current = 0; _logId = 0; }}
                  className="text-[10px] text-[#9CA3AF] hover:text-[#374151] transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
            <div ref={logScrollRef} className="flex-1 overflow-y-auto p-3 space-y-0.5 font-mono text-[11px] leading-relaxed bg-[#FAFBFC]">
              {logEntries.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <p className="text-[#9CA3AF] text-xs text-center">Agent logs will appear here<br />when you send a query</p>
                </div>
              )}
              {logEntries.map(entry => {
                if (entry.isSeparator) return <div key={entry.id} className="border-b border-[#E5E7EB] my-1" />;
                if (entry.isHeader) {
                  const color = getAgentColor(entry.agent);
                  return (
                    <div key={entry.id} className="mt-2 mb-0.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${color.dot} flex-shrink-0`} />
                        <span className="text-[#9CA3AF] text-[10px]">{entry.text}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${color.bg} ${color.text}`}>{entry.agent}</span>
                      </div>
                      <div className="ml-3.5 text-[#1C1C1E] font-semibold text-[11px]">{entry.label}</div>
                    </div>
                  );
                }
                if (!entry.text) return null;
                return <div key={entry.id} className="ml-3.5 text-[#6B7280] whitespace-pre-wrap break-all">{entry.text}</div>;
              })}
              <div className="h-1" />
            </div>
            <div className="px-4 py-2 border-t border-[#F1F5F9] flex flex-wrap gap-2">
              {Object.entries(AGENT_COLORS).filter(([k]) => k !== 'GLOBAL').map(([name, c]) => (
                <div key={name} className="flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                  <span className="text-[9px] text-[#6B7280]">{name.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </aside>
        )}
        */}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingSessionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={handleCancelDelete}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-[#1C1C1E] mb-2">Delete Chat?</h3>
            <p className="text-sm text-[#6B7280] mb-6">
              Are you sure you want to delete this chat session? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm font-medium text-[#6B7280] hover:text-[#374151] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-[#EF4444] hover:bg-[#DC2626] rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareData && (
        <ChatShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareUrl={shareData.share_url}
          messageCount={shareData.message_count}
          viewCount={shareData.view_count}
          forkCount={shareData.fork_count}
          onRevoke={handleRevokeShare}
          isRevoking={isRevokingShare}
        />
      )}
      </div>
    </div>
  );
};

export default AskNaturalOrganicChat;
